// Mock Data for Table Booking Feature
// Based on CUSTOMER_PORTAL_DESIGN_SPEC_2025-12-21.md

export interface Organizer {
  id: string;
  name: string;
  logo?: string;
  description: string;
  address: string;
  city: string;
  phone: string;
  email: string;
  rating: number; // 0-5
  reviewCount: number;
  branchCount: number;
  status: 'active' | 'inactive';
  image?: string;
}

export interface Branch {
  id: string;
  organizerId: string;
  name: string;
  address: string;
  phone: string;
  email: string;
  status: 'active' | 'inactive';
  tableCount: number;
  availableTables: number;
  images?: string[];
}

export interface Table {
  id: string;
  branchId: string;
  number: number;
  capacity: number;
  status: 'available' | 'occupied' | 'reserved' | 'maintenance';
  location?: string;
  category?: string; // Main Hall, VIP Section, Outdoor Area, etc.
  images?: string[];
  imageUrl?: string; // Organizer-uploaded photo of the table's position, so customers recognise the spot
  features?: string[]; // ['Window View', 'TV Screen', 'Quiet Area']
  isTableYours?: boolean; // Indicates if this table belongs to the current customer (approved/active booking)
  isCheckedIn?: boolean; // Customer has actually checked in (arrived) at this table — gates "Call staff"
  isBookedByYou?: boolean; // Indicates if customer has a pending booking request for this table
  isAssignedToYou?: boolean; // Indicates if customer is assigned to a chair at this table by the organizer
  assignedBookingId?: string; // The main booking ID for the chair assignment
  isBookingOpened?: boolean; // Table has open booking (organizer allows others to request seats)
  seatRequestStatus?: 'pending' | 'approved' | 'rejected' | null; // Customer's seat request status for this table
  openForJoinBookingId?: string; // Booking ID for requesting a seat (from open-for-join API)
}

export interface CustomerBooking {
  id: string;
  tableId: string;
  tableNumber: string;
  organizerId: string;
  organizerName: string;
  branchId: string;
  branchName: string;
  branchAddress: string;
  customerId: string;
  customerName: string;
  customerPhone: string;
  customerEmail: string;
  bookingTime: string;
  numberOfGuests: number;
  specialRequests?: string;
  status: 'pending' | 'accepted' | 'rejected' | 'cancelled' | 'completed';
  requestedAt: string;
  acceptedAt?: string;
  rejectedAt?: string;
  organizerResponse?: {
    comments?: string;
    suggestions?: string;
    menuDetails?: string;
    rejectionReason?: string;
  };
  arrivalTime?: string;
  completedAt?: string;
}

export interface TableSession {
  id: string;
  bookingId: string;
  tableId: string;
  tableNumber: string;
  branchId: string;
  branchName: string;
  customerId: string;
  customerName: string;
  numberOfGuests: number;
  actualGuests?: number;
  arrivalTime: string;
  status: 'active' | 'completed';
  currentOrderId?: string;
}

export interface MenuItem {
  id: string;
  category: string;
  name: string;
  description?: string;
  image?: string;
  basePrice: number;
  variants?: {
    type?: string[]; // ['Normal', 'Cool', 'Hot']
    brand?: string[]; // ['Coca-Cola', 'Pepsi', 'Sprite']
    size?: string[]; // ['Small', 'Medium', 'Large']
  };
  available: boolean;
}

export interface OrderItem {
  id: string;
  menuItemId: string;
  name: string;
  variant?: string;
  brand?: string;
  quantity: number;
  unitPrice: number;
  totalPrice: number;
}

export interface Order {
  id: string;
  sessionId: string;
  tableId: string;
  tableNumber: string;
  customerId: string;
  items: OrderItem[];
  subtotal: number;
  tax: number;
  total: number;
  status: 'pending' | 'preparing' | 'ready' | 'completed' | 'cancelled';
  placedAt: string;
  estimatedReadyTime?: string;
  completedAt?: string;
  paymentStatus: 'pending' | 'paid';
  paymentMethod?: 'cash' | 'card' | 'digital_wallet';
}

export interface ChatMessage {
  id: string;
  sessionId: string;
  senderId: string;
  senderType: 'customer' | 'organizer' | 'system';
  senderName: string;
  message: string;
  timestamp: string;
  read: boolean;
  attachments?: {
    type: 'image' | 'file';
    url: string;
    name?: string;
  }[];
}

export interface Feedback {
  id: string;
  sessionId: string;
  bookingId: string;
  organizerId: string;
  organizerName: string;
  branchId: string;
  branchName: string;
  tableId: string;
  tableNumber: string;
  customerId: string;
  customerName: string;
  overallRating: number; // 1-5
  categoryRatings: {
    service: number;
    food: number;
    ambiance: number;
    value: number;
  };
  positiveFeedback?: string;
  improvementFeedback?: string;
  additionalComments?: string;
  photos?: string[];
  submittedAt: string;
  organizerResponse?: {
    message: string;
    respondedAt: string;
  };
}

// Mock Data
export const MOCK_ORGANIZERS: Organizer[] = [
  {
    id: 'org-1',
    name: 'Downtown Billiards Club',
    logo: 'https://images.unsplash.com/photo-1551882547-ff40c63fe5fa?w=200&h=200&fit=crop&auto=format',
    description: 'Premium billiards experience in the heart of downtown',
    address: '123 Main Street',
    city: 'Downtown',
    phone: '+1 (555) 123-4567',
    email: 'info@downtownbilliards.com',
    rating: 4.5,
    reviewCount: 127,
    branchCount: 3,
    status: 'active',
    image: 'https://images.unsplash.com/photo-1551882547-ff40c63fe5fa?w=600&h=300&fit=crop&auto=format'
  },
  {
    id: 'org-2',
    name: 'Elite Pool Hall',
    logo: 'https://images.unsplash.com/photo-1578662996442-48f60103fc96?w=200&h=200&fit=crop&auto=format',
    description: 'Professional pool tables and tournament-grade equipment',
    address: '456 Oak Avenue',
    city: 'Midtown',
    phone: '+1 (555) 234-5678',
    email: 'contact@elitepool.com',
    rating: 4.8,
    reviewCount: 89,
    branchCount: 2,
    status: 'active',
    image: 'https://images.unsplash.com/photo-1578662996442-48f60103fc96?w=600&h=300&fit=crop&auto=format'
  },
  {
    id: 'org-3',
    name: 'Sports Bar & Billiards',
    logo: 'https://images.unsplash.com/photo-1551882547-ff40c63fe5fa?w=200&h=200&fit=crop&auto=format',
    description: 'Great food, drinks, and billiards in a relaxed atmosphere',
    address: '789 Pine Street',
    city: 'Uptown',
    phone: '+1 (555) 345-6789',
    email: 'hello@sportsbilliards.com',
    rating: 4.2,
    reviewCount: 203,
    branchCount: 5,
    status: 'active',
    image: 'https://images.unsplash.com/photo-1551882547-ff40c63fe5fa?w=600&h=300&fit=crop&auto=format'
  }
];

export const MOCK_BRANCHES: Branch[] = [
  {
    id: 'branch-1',
    organizerId: 'org-1',
    name: 'Downtown Branch',
    address: '123 Main Street, Downtown, City 12345',
    phone: '+1 (555) 123-4567',
    email: 'downtown@organizer.com',
    status: 'active',
    tableCount: 12,
    availableTables: 8,
    images: [
      'https://images.unsplash.com/photo-1551882547-ff40c63fe5fa?w=800&h=400&fit=crop&auto=format',
      'https://images.unsplash.com/photo-1578662996442-48f60103fc96?w=800&h=400&fit=crop&auto=format'
    ]
  },
  {
    id: 'branch-2',
    organizerId: 'org-1',
    name: 'Riverside Branch',
    address: '456 River Road, Riverside, City 12346',
    phone: '+1 (555) 123-4568',
    email: 'riverside@organizer.com',
    status: 'active',
    tableCount: 10,
    availableTables: 6,
    images: [
      'https://images.unsplash.com/photo-1551882547-ff40c63fe5fa?w=800&h=400&fit=crop&auto=format'
    ]
  },
  {
    id: 'branch-3',
    organizerId: 'org-2',
    name: 'Elite Main Branch',
    address: '456 Oak Avenue, Midtown, City 12347',
    phone: '+1 (555) 234-5678',
    email: 'main@elitepool.com',
    status: 'active',
    tableCount: 15,
    availableTables: 10,
    images: [
      'https://images.unsplash.com/photo-1578662996442-48f60103fc96?w=800&h=400&fit=crop&auto=format'
    ]
  }
];

export const MOCK_TABLES: Table[] = [
  {
    id: 'table-1',
    branchId: 'branch-1',
    number: 1,
    capacity: 4,
    status: 'available',
    location: 'Main Hall',
    category: 'Main Hall',
    images: ['https://images.unsplash.com/photo-1551882547-ff40c63fe5fa?w=400&h=300&fit=crop&auto=format'],
    features: ['Window View', 'TV Screen']
  },
  {
    id: 'table-2',
    branchId: 'branch-1',
    number: 2,
    capacity: 6,
    status: 'occupied',
    location: 'Main Hall',
    category: 'Main Hall',
    images: ['https://images.unsplash.com/photo-1578662996442-48f60103fc96?w=400&h=300&fit=crop&auto=format'],
    features: ['Window View']
  },
  {
    id: 'table-3',
    branchId: 'branch-1',
    number: 3,
    capacity: 4,
    status: 'available',
    location: 'Main Hall',
    category: 'Main Hall',
    images: ['https://images.unsplash.com/photo-1551882547-ff40c63fe5fa?w=400&h=300&fit=crop&auto=format'],
    features: ['TV Screen']
  },
  {
    id: 'table-4',
    branchId: 'branch-1',
    number: 4,
    capacity: 8,
    status: 'reserved',
    location: 'VIP Section',
    category: 'VIP Section',
    images: ['https://images.unsplash.com/photo-1578662996442-48f60103fc96?w=400&h=300&fit=crop&auto=format'],
    features: ['Private Area', 'Premium Seating', 'Large Group']
  },
  {
    id: 'table-5',
    branchId: 'branch-1',
    number: 5,
    capacity: 6,
    status: 'available',
    location: 'VIP Section',
    category: 'VIP Section',
    images: ['https://images.unsplash.com/photo-1578662996442-48f60103fc96?w=400&h=300&fit=crop&auto=format'],
    features: ['Private Area', 'Premium Seating']
  },
  {
    id: 'table-6',
    branchId: 'branch-1',
    number: 6,
    capacity: 2,
    status: 'available',
    location: 'Outdoor Area',
    category: 'Outdoor Area',
    images: ['https://images.unsplash.com/photo-1551882547-ff40c63fe5fa?w=400&h=300&fit=crop&auto=format'],
    features: ['Outdoor Seating', 'Fresh Air']
  },
  {
    id: 'table-7',
    branchId: 'branch-1',
    number: 7,
    capacity: 4,
    status: 'maintenance',
    location: 'Main Hall',
    category: 'Main Hall',
    images: ['https://images.unsplash.com/photo-1551882547-ff40c63fe5fa?w=400&h=300&fit=crop&auto=format'],
    features: []
  },
  {
    id: 'table-8',
    branchId: 'branch-1',
    number: 8,
    capacity: 4,
    status: 'available',
    location: 'Main Hall',
    category: 'Main Hall',
    images: ['https://images.unsplash.com/photo-1551882547-ff40c63fe5fa?w=400&h=300&fit=crop&auto=format'],
    features: ['Window View']
  }
];

export const MOCK_CUSTOMER_BOOKINGS: CustomerBooking[] = [
  {
    id: 'booking-1',
    tableId: 'table-1',
    tableNumber: '1',
    organizerId: 'org-1',
    organizerName: 'Downtown Billiards Club',
    branchId: 'branch-1',
    branchName: 'Downtown Branch',
    branchAddress: '123 Main Street, Downtown, City 12345',
    customerId: 'customer-1',
    customerName: 'John Doe',
    customerPhone: '+1 (555) 123-4567',
    customerEmail: 'john.doe@example.com',
    bookingTime: '2024-01-20T18:00:00Z',
    numberOfGuests: 4,
    specialRequests: 'Window seat preferred',
    status: 'accepted',
    requestedAt: '2024-01-19T10:00:00Z',
    acceptedAt: '2024-01-19T10:15:00Z',
    organizerResponse: {
      comments: 'We have reserved your table!',
      suggestions: 'Try our special menu items',
      menuDetails: 'Check out our premium drinks menu'
    }
  },
  {
    id: 'booking-2',
    tableId: 'table-3',
    tableNumber: '3',
    organizerId: 'org-1',
    organizerName: 'Downtown Billiards Club',
    branchId: 'branch-1',
    branchName: 'Downtown Branch',
    branchAddress: '123 Main Street, Downtown, City 12345',
    customerId: 'customer-1',
    customerName: 'John Doe',
    customerPhone: '+1 (555) 123-4567',
    customerEmail: 'john.doe@example.com',
    bookingTime: '2024-01-25T19:00:00Z',
    numberOfGuests: 2,
    status: 'pending',
    requestedAt: '2024-01-24T14:00:00Z'
  }
];

export const MOCK_MENU_ITEMS: MenuItem[] = [
  {
    id: 'menu-1',
    category: 'Drinks',
    name: 'Water',
    description: 'Fresh drinking water',
    image: 'https://images.unsplash.com/photo-1548839140-5a4e513ba5d4?w=300&h=300&fit=crop&auto=format',
    basePrice: 2.00,
    variants: {
      type: ['Normal', 'Cool', 'Hot']
    },
    available: true
  },
  {
    id: 'menu-2',
    category: 'Drinks',
    name: 'Soda',
    description: 'Carbonated soft drink',
    image: 'https://images.unsplash.com/photo-1554866585-cd94860890b7?w=300&h=300&fit=crop&auto=format',
    basePrice: 3.50,
    variants: {
      type: ['Normal', 'Cool', 'Diet'],
      brand: ['Coca-Cola', 'Pepsi', 'Sprite', 'Fanta']
    },
    available: true
  },
  {
    id: 'menu-3',
    category: 'Food',
    name: 'Chicken Wings',
    description: 'Spicy chicken wings with sauce',
    image: 'https://images.unsplash.com/photo-1527477396000-e27163b481c2?w=300&h=300&fit=crop&auto=format',
    basePrice: 12.99,
    variants: {
      size: ['6 pieces', '12 pieces', '18 pieces']
    },
    available: true
  },
  {
    id: 'menu-4',
    category: 'Food',
    name: 'French Fries',
    description: 'Crispy golden fries',
    image: 'https://images.unsplash.com/photo-1573080496219-bb080dd4f877?w=300&h=300&fit=crop&auto=format',
    basePrice: 5.99,
    variants: {
      size: ['Small', 'Medium', 'Large']
    },
    available: true
  },
  {
    id: 'menu-5',
    category: 'Snacks',
    name: 'Nachos',
    description: 'Loaded nachos with cheese and jalapeños',
    image: 'https://images.unsplash.com/photo-1513456852971-30c0b8199d4d?w=300&h=300&fit=crop&auto=format',
    basePrice: 8.99,
    available: true
  }
];

export const MOCK_ORDERS: Order[] = [
  {
    id: 'order-1',
    sessionId: 'session-1',
    tableId: 'table-1',
    tableNumber: '1',
    customerId: 'customer-1',
    items: [
      {
        id: 'item-1',
        menuItemId: 'menu-1',
        name: 'Water',
        variant: 'Cool',
        quantity: 2,
        unitPrice: 2.00,
        totalPrice: 4.00
      },
      {
        id: 'item-2',
        menuItemId: 'menu-2',
        name: 'Soda',
        variant: 'Normal',
        brand: 'Coca-Cola',
        quantity: 1,
        unitPrice: 3.50,
        totalPrice: 3.50
      }
    ],
    subtotal: 7.50,
    tax: 1.35,
    total: 8.85,
    status: 'preparing',
    placedAt: '2024-01-20T18:15:00Z',
    estimatedReadyTime: '2024-01-20T18:25:00Z',
    paymentStatus: 'pending'
  }
];

export const MOCK_CHAT_MESSAGES: ChatMessage[] = [
  {
    id: 'msg-1',
    sessionId: 'session-1',
    senderId: 'organizer-1',
    senderType: 'organizer',
    senderName: 'Downtown Branch Staff',
    message: 'Welcome! How can we assist you today?',
    timestamp: '2024-01-20T18:00:00Z',
    read: true
  },
  {
    id: 'msg-2',
    sessionId: 'session-1',
    senderId: 'customer-1',
    senderType: 'customer',
    senderName: 'John Doe',
    message: 'Hi! Can we get the menu please?',
    timestamp: '2024-01-20T18:01:00Z',
    read: true
  },
  {
    id: 'msg-3',
    sessionId: 'session-1',
    senderId: 'organizer-1',
    senderType: 'organizer',
    senderName: 'Downtown Branch Staff',
    message: 'Of course! Here is our menu: [Menu Link]',
    timestamp: '2024-01-20T18:01:30Z',
    read: true
  },
  {
    id: 'msg-4',
    sessionId: 'session-1',
    senderId: 'system',
    senderType: 'system',
    senderName: 'System',
    message: 'Your order #ORD-123 has been placed',
    timestamp: '2024-01-20T18:15:00Z',
    read: true
  }
];

export const MOCK_FEEDBACK: Feedback[] = [
  {
    id: 'feedback-1',
    sessionId: 'session-1',
    bookingId: 'booking-1',
    organizerId: 'org-1',
    organizerName: 'Downtown Billiards Club',
    branchId: 'branch-1',
    branchName: 'Downtown Branch',
    tableId: 'table-1',
    tableNumber: '1',
    customerId: 'customer-1',
    customerName: 'John Doe',
    overallRating: 5,
    categoryRatings: {
      service: 5,
      food: 4,
      ambiance: 5,
      value: 4
    },
    positiveFeedback: 'Great service and friendly staff!',
    improvementFeedback: 'Could improve the menu variety',
    additionalComments: 'Will definitely come back!',
    photos: ['https://images.unsplash.com/photo-1551882547-ff40c63fe5fa?w=400&h=300&fit=crop&auto=format'],
    submittedAt: '2024-01-20T20:00:00Z',
    organizerResponse: {
      message: 'Thank you for your feedback! We appreciate your visit.',
      respondedAt: '2024-01-21T09:00:00Z'
    }
  }
];

// Helper functions to get data
export const getOrganizerById = (id: string): Organizer | undefined => {
  return MOCK_ORGANIZERS.find(org => org.id === id);
};

export const getBranchesByOrganizerId = (organizerId: string): Branch[] => {
  return MOCK_BRANCHES.filter(branch => branch.organizerId === organizerId);
};

export const getBranchById = (id: string): Branch | undefined => {
  return MOCK_BRANCHES.find(branch => branch.id === id);
};

export const getTablesByBranchId = (branchId: string): Table[] => {
  return MOCK_TABLES.filter(table => table.branchId === branchId);
};

export const getTableById = (id: string): Table | undefined => {
  return MOCK_TABLES.find(table => table.id === id);
};

export const getBookingsByCustomerId = (customerId: string): CustomerBooking[] => {
  return MOCK_CUSTOMER_BOOKINGS.filter(booking => booking.customerId === customerId);
};

export const getBookingById = (id: string): CustomerBooking | undefined => {
  return MOCK_CUSTOMER_BOOKINGS.find(booking => booking.id === id);
};

export const getMenuItemsByCategory = (category?: string): MenuItem[] => {
  if (!category) return MOCK_MENU_ITEMS;
  return MOCK_MENU_ITEMS.filter(item => item.category === category);
};

export const getChatMessagesBySessionId = (sessionId: string): ChatMessage[] => {
  return MOCK_CHAT_MESSAGES.filter(msg => msg.sessionId === sessionId);
};

// Mock Tournament Data
export interface MockTournament {
  id: string;
  name: string;
  description?: string;
  startDate: string;
  endDate: string;
  registrationDeadline: string;
  entryFee: number;
  maxParticipants: number;
  currentParticipants: number;
  status: 'upcoming' | 'ongoing' | 'completed' | 'cancelled';
  format?: string;
  prizePool?: number;
  organizerId: string;
  branchId?: string;
}

export const MOCK_TOURNAMENTS: MockTournament[] = [
  {
    id: 'tour-1',
    name: 'Weekend Championship',
    description: 'Join us for an exciting weekend tournament with great prizes!',
    startDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(), // 7 days from now
    endDate: new Date(Date.now() + 9 * 24 * 60 * 60 * 1000).toISOString(), // 9 days from now
    registrationDeadline: new Date(Date.now() + 5 * 24 * 60 * 60 * 1000).toISOString(), // 5 days from now
    entryFee: 500,
    maxParticipants: 32,
    currentParticipants: 18,
    status: 'upcoming',
    format: '8-Ball',
    prizePool: 15000,
    organizerId: 'org-1',
    branchId: 'branch-1'
  },
  {
    id: 'tour-2',
    name: 'Friday Night League',
    description: 'Weekly league tournament every Friday evening',
    startDate: new Date(Date.now() + 2 * 24 * 60 * 60 * 1000).toISOString(), // 2 days from now
    endDate: new Date(Date.now() + 2 * 24 * 60 * 60 * 1000 + 4 * 60 * 60 * 1000).toISOString(), // Same day, 4 hours later
    registrationDeadline: new Date(Date.now() + 1 * 24 * 60 * 60 * 1000).toISOString(), // 1 day from now
    entryFee: 300,
    maxParticipants: 16,
    currentParticipants: 12,
    status: 'upcoming',
    format: '9-Ball',
    prizePool: 8000,
    organizerId: 'org-1',
    branchId: 'branch-1'
  },
  {
    id: 'tour-3',
    name: 'Monthly Masters',
    description: 'Monthly championship for experienced players',
    startDate: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(), // 2 days ago
    endDate: new Date(Date.now() + 1 * 24 * 60 * 60 * 1000).toISOString(), // 1 day from now
    registrationDeadline: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(), // 3 days ago
    entryFee: 750,
    maxParticipants: 24,
    currentParticipants: 24,
    status: 'ongoing',
    format: '8-Ball',
    prizePool: 20000,
    organizerId: 'org-1',
    branchId: 'branch-1'
  },
  {
    id: 'tour-4',
    name: 'Beginner Friendly Tournament',
    description: 'Perfect for new players to learn and compete',
    startDate: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString(), // 14 days from now
    endDate: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000 + 6 * 60 * 60 * 1000).toISOString(), // Same day, 6 hours later
    registrationDeadline: new Date(Date.now() + 12 * 24 * 60 * 60 * 1000).toISOString(), // 12 days from now
    entryFee: 200,
    maxParticipants: 20,
    currentParticipants: 5,
    status: 'upcoming',
    format: '8-Ball',
    prizePool: 5000,
    organizerId: 'org-1',
    branchId: 'branch-2'
  },
  {
    id: 'tour-5',
    name: 'Elite Pro Championship',
    description: 'High-stakes tournament for professional players',
    startDate: new Date(Date.now() + 21 * 24 * 60 * 60 * 1000).toISOString(), // 21 days from now
    endDate: new Date(Date.now() + 23 * 24 * 60 * 60 * 1000).toISOString(), // 23 days from now
    registrationDeadline: new Date(Date.now() + 18 * 24 * 60 * 60 * 1000).toISOString(), // 18 days from now
    entryFee: 1500,
    maxParticipants: 16,
    currentParticipants: 8,
    status: 'upcoming',
    format: '10-Ball',
    prizePool: 50000,
    organizerId: 'org-2',
    branchId: 'branch-3'
  }
];

export const getTournamentsByOrganizerId = (organizerId: string): MockTournament[] => {
  return MOCK_TOURNAMENTS.filter(tour => tour.organizerId === organizerId);
};

export const getTournamentsByBranchId = (branchId: string): MockTournament[] => {
  return MOCK_TOURNAMENTS.filter(tour => tour.branchId === branchId);
};

